import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Cable, Save, Settings2, Wifi } from 'lucide-react';
import {
  PORT_DHCP_LABELS,
  PORT_MODE_LABELS,
  type PortDhcp,
  type DeviceDetail,
  type OnuPort,
  type OnuVlan,
  type Page,
  type PortMode,
  type WanCandidate,
} from '@simpleacs/shared';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';
import { useTasks } from '../../lib/tasks';
import { useSyncedDraft } from '../../lib/useSyncedDraft';
import { Button, Card, EmptyState, Input, Pill, Spinner } from '../../components/ui';
import { ConfigPlanCard } from './ConfigPlanCard';
import { portLinkLabel, portName, portParent } from '../../lib/wan-ports';

interface Draft {
  port: string;
  kind: OnuPort['kind'];
  label: string;
  mode: PortMode;
  pvid: string;
  taggedVlans: number[];
  enabled: boolean;
  /** Who answers DHCP on an Ethernet socket; '' = automatic (SmartOLT's selector, 2026-10-16). */
  dhcp: PortDhcp | '';
}

const toDraft = (port: OnuPort): Draft => ({
  port: port.port,
  kind: port.kind,
  label: port.label ?? '',
  mode: port.mode,
  pvid: port.pvid ? String(port.pvid) : '',
  taggedVlans: port.taggedVlans,
  enabled: port.enabled,
  dhcp: port.dhcp ?? '',
});

/**
 * What "automatic" means for this socket, said so the selector's first choice
 * reads as a fact: from the ONU on a LAN socket or a routed VLAN, no control on
 * a bridge (the operator, 2026-10-16: "set the default config for bridges to no
 * control").
 */
export function automaticDhcpLabel(draft: { mode: PortMode; pvid: string; taggedVlans: number[] }, vlans: OnuVlan[]): string {
  if (draft.mode === 'lan') return 'Automatic — from the ONU';
  const carried = [...(draft.pvid ? [Number(draft.pvid)] : []), ...draft.taggedVlans];
  const routed = carried.some((v) => vlans.find((x) => x.vlanId === v)?.mode === 'route');
  return routed ? 'Automatic — from the ONU' : 'Automatic — no control';
}

/**
 * Per-port VLAN treatment. The modes mirror what the hardware does:
 * access is one untagged VLAN, trunk passes tags through, hybrid is an
 * untagged default plus tagged extras.
 */
/**
 * `base` is the owner's API root — `/devices/<id>` for a subscriber whose ONU
 * has informed an ACS, `/onus/<id>` for one that has not. Both serve the same
 * endpoints, because which VLANs an ONU carries and which socket they come out
 * of is the same question either way; only the OLT-only case used to have
 * nowhere to answer it.
 */
export function PortsTab({
  base,
  oltId,
  onuId,
  refresh,
  chassisVendor,
  onApplyWithReboot,
}: {
  base: string;
  oltId?: string;
  onuId?: string;
  /** Re-reads the router's LAN tree — what the link pills show (the page's own `DeviceScopeRefresh`, scope `lan`). */
  refresh?: ReactNode;
  /** The chassis make: the DHCP selector is a ZTE line, so it is offered on a ZTE chassis only. */
  chassisVendor?: string | null;
  /**
   * The page's Resync in the reboot window (2026-10-16): offered when a save
   * shows the ONT declined its DHCP word while up, so every socket changed
   * lands in one reboot — "change on multiple ports then apply to reboot".
   */
  onApplyWithReboot?: () => void;
}) {
  // A save answered with the ONT's decline of the DHCP entity (a Huawei ONT
  // behind a ZTE chassis): the chassis keeps its old word until written while
  // the ONT is off the PON.
  const [dhcpDeclined, setDhcpDeclined] = useState(false);
  const { can } = useSession();
  // §8.6.2: the ONU-config write is `onu.provision`, not `olt.manage` — the API
  // set `writeCap = 'onu.provision'` on 2026-08-30, and org_engineer (whose role
  // is "authorise, resync, reboot and configure ONUs") does NOT hold olt.manage,
  // so this tab rendered read-only for the very role meant to use it while the
  // API would have taken the write. VlansTab uses the identical gate.
  const editable = can(base.startsWith('/onus/') ? 'onu.provision' : 'device.write');

  const portsQuery = useQuery({
    queryKey: ['onu-config', base, 'ports'],
    queryFn: () => api.get<Page<OnuPort>>(`${base}/ports`),
  });
  const vlansQuery = useQuery({
    queryKey: ['onu-config', base, 'vlans'],
    queryFn: () => api.get<Page<OnuVlan>>(`${base}/vlans`),
  });
  /*
    The router's WAN connections — the parents the ports hang off (2026-10-14).
    The page's own query, so a WAN built from the card is seen here at once.
  */
  const routerLinked = base.startsWith('/devices/');
  const deviceQuery = useQuery({
    queryKey: ['device', decodeURIComponent(base.slice('/devices/'.length))],
    queryFn: () => api.get<DeviceDetail>(base),
    enabled: routerLinked,
  });
  const candidates: WanCandidate[] = deviceQuery.data?.wan.candidates ?? [];

  /*
    Every port: the LAN sockets AND the router's WiFi networks (operator,
    2026-10-14: "treat all lan ports and wifi as ports … add wifi ports to the
    ports page"). The WAN card's Ports row edits the same records — which port
    rides which WAN — so the two are one thing seen two ways.
  */
  const ports = portsQuery.data?.items ?? [];
  /**
   * The management VLAN is not shown here and cannot be attached to anything.
   *
   * It is the platform's own path to the ONU, not a service the subscriber
   * carries — it runs in the background and is changed from the Management
   * button on the ONU. Listing it beside the subscriber VLANs invited putting
   * it on a socket, which hands the customer the route we manage them by.
   */
  const vlans = (vlansQuery.data?.items ?? []).filter((v) => v.purpose !== 'management');

  // Every socket the ONU has is listed by the API, configured or not, so there
  // is nothing here to add — only ports waiting to be given a treatment.
  const configured = ports.filter((p) => p.configured).length;
  // VLANs the record puts ports on while the router carries no WAN for them.
  const orphanVlans = deviceQuery.data
    ? [...new Set(ports.map((p) => portParent(p, candidates)?.missingVlan).filter((v): v is number => v !== null && v !== undefined))]
    : [];

  return (
    <div className="space-y-5">
      <Card
        title={
          <span className="flex items-center gap-2">
            <Cable className="size-4 text-muted" />
            Ports
            <span className="text-xs font-normal text-faint">
              {configured} of {ports.length} configured
            </span>
          </span>
        }
        action={refresh}
      >
        {portsQuery.isLoading ? (
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        ) : ports.length === 0 ? (
          <EmptyState title="No ports" description="This ONU has not reported any interfaces." />
        ) : (
          <div className="divide-y divide-line">
            {dhcpDeclined && (
              <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-sm text-warn">
                <span>
                  This ONT does not take DHCP control while it is up (it declined the chassis's word), so the chassis kept its old one.
                  It takes it while rebooting: change every socket you need first, then apply them all in one reboot.
                </span>
                {onApplyWithReboot && (
                  <Button variant="primary" size="sm" onClick={onApplyWithReboot}>
                    Apply with reboot
                  </Button>
                )}
              </div>
            )}
            {orphanVlans.length > 0 && (
              <p className="px-5 py-3 text-sm text-warn">
                VLAN {orphanVlans.join(', ')} {orphanVlans.length === 1 ? 'has' : 'have'} ports on it but no WAN on the router. Ports
                hang off a WAN: apply the VLAN bridged from WAN mode, or Resync, to build it — its ports then attach to it.
              </p>
            )}
            {ports.map((port) => (
              <PortRow
                key={port.port}
                base={base}
                draft={toDraft(port)}
                port={port}
                parent={routerLinked && deviceQuery.data ? portParent(port, candidates) : null}
                configured={port.configured}
                vlans={vlans}
                editable={editable}
                chassisVendor={chassisVendor ?? null}
                onDhcpDeclined={() => setDhcpDeclined(true)}
              />
            ))}
          </div>
        )}
      </Card>

      <ConfigPlanCard base={base} oltId={oltId} onuId={onuId} />
    </div>
  );
}

/**
 * One socket, and whatever this platform holds for it.
 *
 * An unconfigured row is a real port on real hardware that nobody has given a
 * treatment yet, so it is shown alongside the rest rather than behind an "add"
 * button — the port exists either way, and the operator is choosing what it
 * carries, not creating it.
 *
 * Where it starts is decided by the VLANs already attached rather than by a
 * fixed default: a routed VLAN means the ONU terminates the connection and its
 * sockets are the ONU's own LAN, while a bridged one is handed straight out and
 * the port carries it untagged.
 */
function PortRow({
  base,
  draft: initial,
  port,
  parent,
  configured,
  vlans: attached,
  editable,
  chassisVendor,
  onDhcpDeclined,
}: {
  base: string;
  draft: Draft;
  port: OnuPort;
  /** The WAN this port hangs off on the router, or the VLAN it is on with no WAN there. */
  parent: { label: string; missingVlan: number | null; off?: boolean } | null;
  configured: boolean;
  vlans: OnuVlan[];
  editable: boolean;
  chassisVendor: string | null;
  /** The chassis's answer showed the ONT declined the socket's DHCP word (see PortsTab). */
  onDhcpDeclined?: () => void;
}) {
  const wifi = port.kind === 'wifi';
  const Icon = wifi ? Wifi : Cable;
  // A WiFi network rides the router's LAN or a BRIDGED VLAN — never a routed one, never a trunk.
  const vlans = wifi ? attached.filter((v) => v.mode === 'bridge') : attached;
  const modes: PortMode[] = wifi ? ['lan', 'access'] : ['lan', 'access', 'trunk', 'hybrid'];
  const title = portName(port);
  // An operator's own label rides beside the name, never instead of it (2026-10-14: "label the eth ports as lan 1, lan 2").
  const ownLabel = !wifi && port.label && port.label !== title ? port.label : null;
  const tasks = useTasks();
  // Guard the draft against a ports refetch landing mid-edit (a sibling port's
  // save, or a config-plan action, invalidates this list). See useSyncedDraft.
  const { draft, baseline, edit, reset } = useSyncedDraft<Draft>(initial, [
    initial.port,
    initial.mode,
    initial.pvid,
    initial.enabled,
    initial.dhcp,
  ]);
  // Collapsed by default: four open editors is noise when the usual visit only
  // reads. Configure opens exactly the port being worked on.
  const [configuring, setConfiguring] = useState(false);

  /**
   * A mode that needs an untagged VLAN gets one.
   *
   * The list no longer opens on a "Select…" row, so an empty pvid would show
   * the first VLAN while the draft held nothing — and Save would come back
   * with "needs an untagged VLAN" about the VLAN on screen.
   */
  useEffect(() => {
    if (draft.mode !== 'access' && draft.mode !== 'hybrid') return;
    if (draft.pvid || vlans.length === 0) return;
    edit((d) => ({ ...d, pvid: String(vlans[0]!.vlanId) }));
  }, [draft.mode, draft.pvid, vlans, edit]);

  // An unconfigured port has nothing stored, so its defaults are already a
  // change worth saving — otherwise Save would sit disabled on a row that has
  // never been written.
  const dirty = !configured || JSON.stringify(draft) !== JSON.stringify(baseline);

  // Saving a port writes to the chassis, so — like resync and the VLAN attach —
  // it CLOSES the editor on submit and runs in the tasks tray rather than pinning
  // the row to a spinner; the tray carries the outcome (and keeps a chassis
  // refusal until it is acknowledged). The collapsed row re-reads its value once
  // the write lands and the ports list is invalidated.
  const name = draft.label || draft.port;
  function savePort() {
    const payload = {
      port: draft.port,
      kind: draft.kind,
      label: draft.label || null,
      mode: draft.mode,
      pvid: draft.pvid ? Number(draft.pvid) : null,
      taggedVlans: draft.taggedVlans,
      enabled: draft.enabled,
      ...(wifi ? {} : { dhcp: draft.dhcp || null }),
    };
    setConfiguring(false);
    type SaveAnswer = OnuPort & { router?: string[]; applied?: { warnings?: string[] } | null; dhcpDeclined?: boolean };
    tasks.start({
      label: `Save port ${name}`,
      run: async () => {
        const r = await api.put<SaveAnswer>(`${base}/ports`, payload);
        // The ONT declined the DHCP word while up (a Huawei ONT behind a ZTE
        // chassis): the record holds the choice, the chassis does not yet.
        const declined = !wifi && (r.applied?.warnings ?? []).some((w) => /DHCP-direction entity/i.test(w));
        if (declined) onDhcpDeclined?.();
        return { ...r, dhcpDeclined: declined };
      },
      done: (r) =>
        [
          `Port ${name} saved.`,
          ...(r.router ?? []),
          ...(r.dhcpDeclined ? ['The ONT declined its DHCP word while up — use Apply with reboot on the Ports tab to land it.'] : []),
        ].join(' '),
      invalidate: [
        ['onu-config', base, 'ports'],
        ['onu-config', base, 'config-plan'],
      ],
    });
  }

  const toggleTagged = (vlanId: number) =>
    edit({
      ...draft,
      taggedVlans: draft.taggedVlans.includes(vlanId)
        ? draft.taggedVlans.filter((v) => v !== vlanId)
        : [...draft.taggedVlans, vlanId].sort((a, b) => a - b),
    });

  if (!configuring) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 p-5">
        <span className="flex items-center gap-2">
          <Icon className="size-4 text-muted" />
          <span className="font-medium text-ink">{title}</span>
          {ownLabel && <span className="text-xs text-muted">{ownLabel}</span>}
          <span className="font-mono text-xs text-faint">{initial.port}</span>
          {wifi && port.network?.band && <span className="text-xs text-faint">{port.network.band}</span>}
          {wifi && port.network?.on === false && <Pill tone="neutral">off on the router</Pill>}
          <Pill state tone={!configured ? 'neutral' : initial.enabled ? 'success' : 'neutral'}>
            {!configured
              ? wifi && port.network
                ? port.network.bridgeVlan !== null
                  ? `On VLAN ${port.network.bridgeVlan} at the router — not recorded`
                  : 'On the LAN at the router — not recorded'
                : 'Not configured'
              : !initial.enabled
                ? 'disabled'
                : initial.mode === 'lan'
                  ? wifi
                    ? 'LAN — on the router’s own network'
                    : 'LAN'
                  : initial.mode === 'access'
                    ? `Access VLAN ${initial.pvid || '—'}`
                    : initial.mode === 'trunk'
                      ? 'Trunk'
                      : `Hybrid ${initial.pvid || '—'} + ${initial.taggedVlans.join(',') || 'none'}`}
          </Pill>
          {/* The operator's own DHCP choice on the socket, when one is set (SmartOLT's selector, 2026-10-16). */}
          {!wifi && initial.dhcp ? <Pill tone="neutral">DHCP {PORT_DHCP_LABELS[initial.dhcp].toLowerCase()}</Pill> : null}
          {/* The socket's link as the router last reported it (2026-10-14): plugged in, the negotiated rate, duplex. */}
          {(() => {
            const link = portLinkLabel(port.link);
            return link ? (
              <Pill state tone={link.up ? 'success' : 'neutral'} dot>
                {link.text}
              </Pill>
            ) : null;
          })()}
          {parent?.off ? (
            <Pill tone="neutral">off — on no WAN</Pill>
          ) : parent?.missingVlan !== null && parent?.missingVlan !== undefined ? (
            <Pill state tone="warn">no WAN on VLAN {parent.missingVlan}</Pill>
          ) : parent ? (
            <Pill tone="neutral">on {parent.label}</Pill>
          ) : null}
        </span>
        {editable && (
          <Button
            size="sm"
            icon={<Settings2 className="size-3.5" />}
            onClick={() => setConfiguring(true)}
          >
            Configure
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <span className="flex items-center gap-2">
          <Icon className="size-4 text-muted" />
          <span className="font-medium text-ink">{title}</span>
          <span className="font-mono text-xs text-faint">{draft.port}</span>
          {!draft.enabled && <Pill tone="neutral">disabled</Pill>}
        </span>

        {editable && (
          <label className="flex cursor-pointer items-center gap-2 text-xs text-muted">
            <input
              type="checkbox"
              checked={draft.enabled}
              onChange={(e) => edit({ ...draft, enabled: e.target.checked })}
            />
            Enabled
          </label>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-[auto_10rem_1fr]">
        <div>
          <span className="label">Mode</span>
          <div className="flex gap-4">
            {modes.map((mode) => (
              <label
                key={mode}
                className={`flex items-center gap-2 text-sm ${
                  editable ? 'cursor-pointer text-ink' : 'text-faint'
                }`}
              >
                <input
                  type="radio"
                  name={`mode-${draft.port}`}
                  checked={draft.mode === mode}
                  disabled={!editable}
                  onChange={() => edit({ ...draft, mode })}
                />
                {PORT_MODE_LABELS[mode]}
              </label>
            ))}
          </div>
        </div>

        <div>
          <span className="label">
            {draft.mode === 'hybrid' ? 'Default (untagged)' : 'Untagged VLAN'}
          </span>
          {draft.mode === 'lan' ? (
            <span className="block pt-2 text-xs text-faint">
              Behind the ONU&rsquo;s router — no VLAN
            </span>
          ) : wifi && vlans.length === 0 ? (
            <span className="block pt-2 text-xs text-faint">Attach a bridged VLAN first</span>
          ) : draft.mode === 'trunk' ? (
            <span className="block pt-2 text-xs text-faint">Tags pass through</span>
          ) : (
            <select
              className="input"
              value={draft.pvid}
              disabled={!editable}
              onChange={(e) => edit({ ...draft, pvid: e.target.value })}
            >
              {/* No placeholder row: the list is the VLANs attached to this
                  ONU, and an empty one is caught by the API rather than
                  offered as something to pick. */}
              {vlans.map((vlan) => (
                <option key={vlan.id} value={vlan.vlanId}>
                  {vlan.vlanId}
                  {vlan.label ? ` — ${vlan.label}` : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        <div>
          <span className="label">Tagged VLANs</span>
          {draft.mode !== 'hybrid' ? (
            <span className="block pt-2 text-xs text-faint">
              {draft.mode === 'trunk' ? 'All VLANs the ONU carries' : 'None in access mode'}
            </span>
          ) : vlans.length === 0 ? (
            <span className="block pt-2 text-xs text-faint">Attach VLANs first</span>
          ) : (
            <div className="flex flex-wrap gap-2 pt-1.5">
              {vlans
                .filter((vlan) => String(vlan.vlanId) !== draft.pvid)
                .map((vlan) => (
                  <label
                    key={vlan.id}
                    className={`flex items-center gap-1.5 rounded-lg border px-2 py-1 text-xs ${
                      draft.taggedVlans.includes(vlan.vlanId)
                        ? 'border-primary/40 bg-primary/10 text-ink'
                        : 'border-line bg-surface-2 text-muted'
                    } ${editable ? 'cursor-pointer' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={draft.taggedVlans.includes(vlan.vlanId)}
                      disabled={!editable}
                      onChange={() => toggleTagged(vlan.vlanId)}
                    />
                    {vlan.vlanId}
                    {vlan.label ? ` ${vlan.label}` : ''}
                  </label>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Who answers DHCP on the socket — SmartOLT's four choices, which are
          ZTE's `dhcp-ip ethuni` line (2026-10-16). An Ethernet socket on a
          ZTE chassis only: a WiFi network has no socket, a Huawei chassis
          has no such line (its ONT's own DHCP server decides). */}
      {!wifi && chassisVendor !== 'huawei' && (
        <div className="mt-3 md:max-w-sm">
          <span className="label">DHCP</span>
          <select
            className="input"
            value={draft.dhcp}
            disabled={!editable}
            onChange={(e) => edit({ ...draft, dhcp: e.target.value as Draft['dhcp'] })}
          >
            <option value="">{automaticDhcpLabel(draft, attached)}</option>
            {(Object.keys(PORT_DHCP_LABELS) as PortDhcp[]).map((k) => (
              <option key={k} value={k}>
                {PORT_DHCP_LABELS[k]}
              </option>
            ))}
          </select>
          <span className="block pt-1 text-xs text-faint">
            Who answers DHCP requests arriving on this socket: the ISP upstream, the ONU itself, nobody (forbidden), or left to the chassis (no control).
          </span>
        </div>
      )}

      {editable && (
        <div className="mt-3 flex gap-2">
          <Button
            variant="primary"
            size="sm"
            icon={<Save className="size-3.5" />}
            disabled={!dirty}
            onClick={savePort}
          >
            Save port
          </Button>
          <Button
            size="sm"
            onClick={() => {
              reset();
              setConfiguring(false);
            }}
          >
            Close
          </Button>
        </div>
      )}
    </div>
  );
}

export function PortLabelInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return <Input value={value} onChange={(e) => onChange(e.target.value)} />;
}
